/**
 * IntroductionRequestForm Component
 * Request introduction via Pharmunity platform
 */

import { useState } from 'react';
import { useExistingIntroduction } from '../hooks/useIntroductions';
import { introductionService } from '../services/introductionService';
import {
  Users,
  Send,
  AlertCircle,
  CheckCircle,
  Loader2,
  X,
  Building2,
  ArrowRight,
  Info,
  Sparkles
} from 'lucide-react';

export function IntroductionRequestForm({
  sellerId,
  buyer,
  assetId = null,
  assetName = '',
  onSuccess,
  onCancel,
  className = ''
}) {
  const [requestMessage, setRequestMessage] = useState(
    `I'm interested in exploring potential partnership opportunities with ${buyer?.company_name || 'this company'}.

Our ${assetName ? `${assetName} asset` : 'pipeline'} aligns well with their strategic focus areas. I would appreciate Pharmunity facilitating an introduction to their BD team.

Key points I'd like to discuss:
- Strategic fit and synergies
- Partnership structure options
- Next steps for evaluation`
  );
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const { existingRequest, loading: checkingExisting } = useExistingIntroduction(sellerId, buyer?.id);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!requestMessage.trim()) {
      setError('Please enter a message describing your interest');
      return;
    }

    setSending(true);
    setError(null);

    try {
      await introductionService.createRequest({
        sellerId,
        buyerId: buyer.id,
        connectorUserId: null, // Pharmunity is the connector
        relationshipToConnector: 'platform',
        connectorRelationshipToTarget: 'platform',
        requestMessage: requestMessage.trim(),
        assetId
      });

      setSuccess(true);
      setTimeout(() => {
        if (onSuccess) onSuccess();
      }, 2000);
    } catch (err) {
      console.error('Error creating introduction request:', err);
      setError(err.message || 'Failed to send introduction request');
    } finally {
      setSending(false);
    }
  };

  // Show loading state
  if (checkingExisting) {
    return (
      <div className={`bg-white rounded-xl border border-gray-200 p-8 ${className}`}>
        <div className="flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
        </div>
      </div>
    );
  }

  // Show existing request warning
  if (existingRequest) {
    return (
      <div className={`bg-white rounded-xl border border-gray-200 p-6 ${className}`}>
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-medium text-gray-900">Request Already Submitted</h3>
            <p className="text-sm text-gray-600 mt-1">
              You already have a {existingRequest.status} introduction request to {buyer.company_name}.
              {existingRequest.status === 'pending' && ' Our team is working on it.'}
              {existingRequest.status === 'accepted' && ' We will connect you soon.'}
            </p>
            {onCancel && (
              <button
                onClick={onCancel}
                className="mt-3 text-sm text-purple-600 hover:text-purple-700 font-medium"
              >
                Go Back
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Show success state
  if (success) {
    return (
      <div className={`bg-white rounded-xl border border-gray-200 p-8 text-center ${className}`}>
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="w-8 h-8 text-green-600" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900">Request Submitted!</h3>
        <p className="text-gray-500 mt-2">
          Pharmunity will review your request and facilitate the introduction to {buyer?.company_name}.
          You'll receive an update within 2-3 business days.
        </p>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-xl border border-gray-200 ${className}`}>
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
              <Users className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Request Introduction</h3>
              <p className="text-sm text-gray-500">via Pharmunity</p>
            </div>
          </div>
          {onCancel && (
            <button
              onClick={onCancel}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      {/* Connection visualization */}
      <div className="px-6 py-5 bg-gradient-to-r from-purple-50 to-blue-50 border-b border-gray-200">
        <div className="flex items-center justify-center gap-3">
          {/* Seller (You) */}
          <div className="text-center">
            <div className="w-14 h-14 bg-white border-2 border-purple-200 rounded-full flex items-center justify-center mx-auto shadow-sm">
              <Building2 className="w-7 h-7 text-purple-600" />
            </div>
            <p className="text-xs font-medium text-gray-700 mt-2">Your Company</p>
          </div>

          {/* Arrow to Pharmunity */}
          <ArrowRight className="w-5 h-5 text-gray-400" />

          {/* Pharmunity */}
          <div className="text-center">
            <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-blue-500 rounded-full flex items-center justify-center mx-auto shadow-lg">
              <Sparkles className="w-7 h-7 text-white" />
            </div>
            <p className="text-xs font-bold text-purple-700 mt-2">Pharmunity</p>
          </div>

          {/* Arrow to Buyer */}
          <ArrowRight className="w-5 h-5 text-gray-400" />

          {/* Buyer */}
          <div className="text-center">
            <div className="w-14 h-14 bg-white border-2 border-blue-200 rounded-full flex items-center justify-center mx-auto shadow-sm">
              <Building2 className="w-7 h-7 text-blue-600" />
            </div>
            <p className="text-xs font-medium text-gray-700 mt-2">{buyer?.company_name || 'Target'}</p>
          </div>
        </div>

        <p className="text-center text-sm text-gray-600 mt-4">
          Pharmunity will facilitate the introduction on your behalf
        </p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="p-6 space-y-4">
        {/* Request message */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Tell us about your interest
          </label>
          <textarea
            value={requestMessage}
            onChange={(e) => setRequestMessage(e.target.value)}
            placeholder="Describe why you're interested in connecting with this buyer and what you'd like to discuss..."
            rows={6}
            disabled={sending}
            className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
          />
        </div>

        {/* Asset context */}
        {assetName && (
          <div className="flex items-center gap-2 text-sm text-gray-600 bg-gray-50 rounded-lg px-4 py-2">
            <Info className="w-4 h-4 text-gray-400" />
            <span>Regarding: <span className="font-medium">{assetName}</span></span>
          </div>
        )}

        {/* What happens next */}
        <div className="bg-blue-50 rounded-lg p-4">
          <h4 className="text-sm font-medium text-blue-900 mb-2">What happens next?</h4>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>1. Pharmunity reviews your request</li>
            <li>2. We reach out to {buyer?.company_name}'s BD team</li>
            <li>3. If interested, we arrange the introduction</li>
            <li>4. You receive an update within 2-3 business days</li>
          </ul>
        </div>

        {/* Error */}
        {error && (
          <div className="flex items-center gap-2 text-red-600 text-sm">
            <AlertCircle className="w-4 h-4" />
            <span>{error}</span>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-end gap-3 pt-2">
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              disabled={sending}
              className="px-4 py-2 text-gray-600 hover:text-gray-900 font-medium rounded-lg hover:bg-gray-100"
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            disabled={sending}
            className="flex items-center gap-2 px-6 py-2 bg-purple-600 text-white font-medium rounded-lg hover:bg-purple-700 disabled:opacity-50"
          >
            {sending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <Send className="w-4 h-4" />
                Submit Request
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

export default IntroductionRequestForm;
